import './App.scss';
import { ucFirst } from '../Utils/caseconverters';
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Logo from '../Logo/Logo';
import Loader from '../Loader/Loader';
import CampaignLink from '../CampaignLink/CampaignLink';
import ShareButton from '../ShareButton/ShareButton';
import ResultModal from '../ResultModal/ResultModal';
import Confetti from 'react-confetti';
import TransportforetagenLogoNegative from '../../images/tf-logo-neg.svg';
import { ReactComponent as TransportforetagenLogo } from '../../images/tf-logo.svg';

function App() {
    const [fetching, setFetching] = useState(true);
    const [scrollSlidesFromRight, setScrollSlidesFromRight] = useState(true);
    const [data, setData] = useState([]);
    const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
    const [previousScreenIndex, setPreviousScreenIndex] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [userName, setUserName] = useState("");
    const [answers, setAnswers] = useState([]);
    const [showResultModal, setShowResultModal] = useState(false);

    const screensRef = useRef(null);
    const infoScreens = ["ScreenInfo", "ScreenName", "ScreenStart", "ScreenResult"];
    const showProgressBar = !!data.screens && currentScreenIndex !== 0 && currentScreenIndex !== data.screens.length - 1
    const progressStyle = !showProgressBar ? null : { '--progress-width': `${((currentScreenIndex / (data.screens.length - 1)) * 100).toFixed(2)}%` };
    const DIRECTION = {
        FORWARD: {
            value: 1,
            name: "forward"
        },
        BACKWARD: {
            value: -1,
            name: "backward"
        }
    };

    const isQuestionType = (type) => {
        return !infoScreens.includes(type);
    }

    const totalQuestions = !!data.screens ? [...data.screens].filter(item => isQuestionType(item.component)).length : 0;

    const handleNavigation = (direction) => {
        const directionValue = direction.value;
        const newScreenIndex = currentScreenIndex + directionValue;
        setCurrentScreenIndex(newScreenIndex);
        const nextScreenIsQuestion = isQuestionType(data.screens[newScreenIndex].component);
        if(direction === DIRECTION.FORWARD){
            window.dataLayer.push({
                "event": "campaignFormSteps",
                "Steps": `${newScreenIndex}/${screens.length - 1}`
            });
        }

        if (nextScreenIsQuestion) {
            const newQuestionIndex = currentQuestion + directionValue < 0 ? 0 : currentQuestion + directionValue;
            setCurrentQuestion(newQuestionIndex);
        }

        screensRef.current.scrollTop = 0;
    }

    const handleAnswerOnNextQuestion = (index, newAnswers, isImageChoice = false) => {
        const indexExists = answers.some(item => item.index === index);

        if (indexExists) {
            const updatedAnswers = answers.map(item => item.index === index
                ? { ...item, answers: newAnswers, isImageChoice }
                : item
            );
            setAnswers(updatedAnswers);
        } else {
            setAnswers([...answers, { index, answers: newAnswers, isImageChoice }]);
        }
    };

    const restartGame = () => {
        window.dataLayer.push({
            "event": "CampaignButtonClick",
            "buttonText": "Börja om",
            "isAuthenticated": window.authenticated,
            "selectedBranch": undefined,
        });

        setUserName("");
        setAnswers([]);
        setCurrentScreenIndex(0);
        setCurrentQuestion(0);
        setData([]);
        setFetching(true);
        fetchData();
    }

    const fetchData = async () => {
        if (window.API_FETCH_URL) {
            const delay = new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * (1000 - 500 + 1) + 500)));
            await delay;

            const fetchUrl = window.isOptimizelyPreview ? `${window.API_FETCH_URL}?preview=true` : window.API_FETCH_URL;

            const response = await fetch(fetchUrl);
            const json = await response.json();
            setData(json);
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setPreviousScreenIndex(currentScreenIndex);
        setScrollSlidesFromRight(currentScreenIndex >= previousScreenIndex)
    }, [currentScreenIndex]);

    const {
        colorTheme,
        subjectTheme,
        logo,
        screens,
        showCurve,
        campaign,
        share,
        showConfettiOnLastScreen,
        form,
    } = data

    if (fetching || !screens?.length) {
        return (
            <LoadingScreen />
        )
    }

    const isLightTheme = currentScreenIndex !== 0 && currentScreenIndex !== screens.length - 1;

    return (
        <main className={classNames(
            "App",
            "App--FadeIn",
            `App--${ucFirst(colorTheme)}`, {
            "App--Light": isLightTheme,
            "App--Start": currentScreenIndex === 0,
            "App--Final": currentScreenIndex === screens.length - 1
        })}>
            <div
                className={classNames(
                    "App__Modal", {
                    "App__Modal--Show": showResultModal,
                })}
                id="result-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Resultatmodal"
            >
                <div className="App__InnerModal">
                    <ResultModal
                        show={showResultModal}
                        colorTheme={colorTheme}
                        questions={!!data.screens ? [...data.screens].filter(item => isQuestionType(item.component)) : []}
                        answers={answers}
                        screens={screens}
                        userName={userName}
                        onClose={() => setShowResultModal(!showResultModal)}
                    />
                </div>
            </div>
            <div className={classNames("App__Container", {
                "App__Container--Welcome": currentScreenIndex === 0,
            }
            )}>
                {showConfettiOnLastScreen && currentScreenIndex === screens.length - 1 && (
                    <Confetti
                        recycle={false}
                        numberOfPieces={500}
                    />
                )}
                <div className={classNames("App__Logo", {
                    "App__Logo--Small": currentScreenIndex !== 0
                })}>
                    <Logo
                        {...logo}
                        colorTheme={colorTheme}
                    />
                </div>
                <div className={classNames("App__Menu", { ["App__Menu--Bordered"]: !showProgressBar })}>
                    <div className="App__MenuRow">
                        <a aria-label="Gå till Transportföretagen.se" href="/">
                            <img
                                alt="Transportföretagens logotyp"
                                src={TransportforetagenLogoNegative}
                                className="App__MenuLogo"
                            />
                        </a>
                    </div>
                    <div className="App__MenuRow">
                        <ShareButton
                            colorTheme={colorTheme}
                            light={isLightTheme}
                            {...share}
                        />
                        {campaign && (
                            <CampaignLink
                                colorTheme={colorTheme}
                                light={isLightTheme}
                                {...campaign}
                            />
                        )}
                    </div>
                    {showProgressBar && (<div className={"App__ProgressBar"} style={progressStyle} />)}
                </div>
                {screens && (
                    <div ref={screensRef} id="scrollContainer" className={classNames("App__Screens", {
                        "App__Screens--Gaming": currentScreenIndex !== 0
                    })}>
                        <Screens
                            logo={logo}
                            handleAnswerOnNextQuestion={handleAnswerOnNextQuestion}
                            screens={screens}
                            currentScreenIndex={currentScreenIndex}
                            currentQuestion={currentQuestion}
                            totalQuestions={totalQuestions}
                            colorTheme={colorTheme}
                            subjectTheme={subjectTheme}
                            userName={userName}
                            setUserName={setUserName}
                            handlePreviousScreenOnClick={() => handleNavigation(DIRECTION.BACKWARD)}
                            handleNextScreenOnClick={() => handleNavigation(DIRECTION.FORWARD)}
                            answers={answers}
                            restartGame={restartGame}
                            form={form}
                            scrollSlidesFromRight={scrollSlidesFromRight}
                            handleOnShowResultModal={() => setShowResultModal(!showResultModal)}
                        />
                    </div>
                )}
            </div>
            <Illustrations subjectTheme={subjectTheme} />
            {showCurve && (
                <div className={`App__Curve App__Curve--${ucFirst(subjectTheme)}`} />
            )}
        </main>
    );
}

const LoadingScreen = () => {
    return (
        <main className="Loading">
            <TransportforetagenLogo />
            <Loader />
        </main>
    )
}

const ScreenStart = React.lazy(() => import("../ScreenStart/ScreenStart"));
const ScreenName = React.lazy(() => import("../ScreenName/ScreenName"));
const ScreenInfo = React.lazy(() => import("../ScreenInfo/ScreenInfo"));
const ScreenSingleChoice = React.lazy(() => import("../ScreenSingleChoice/ScreenSingleChoice"));
const ScreenSingleChoiceWithMedia = React.lazy(() => import("../ScreenSingleChoiceWithMedia/ScreenSingleChoiceWithMedia"));
const ScreenMultiChoice = React.lazy(() => import("../ScreenMultiChoice/ScreenMultiChoice"));
const ScreenMultiChoiceWithMedia = React.lazy(() => import("../ScreenMultiChoiceWithMedia/ScreenMultiChoiceWithMedia"));
const ScreenSingleImageChoice = React.lazy(() => import("../ScreenSingleImageChoice/ScreenSingleImageChoice"));
const ScreenSingleRangeChoice = React.lazy(() => import("../ScreenSingleRangeChoice/ScreenSingleRangeChoice"));
const ScreenMultiImageChoice = React.lazy(() => import("../ScreenMultiImageChoice/ScreenMultiImageChoice"));
const ScreenResult = React.lazy(() => import("../ScreenResult/ScreenResult"));

const Screens = ({
    logo,
    screens,
    handleAnswerOnNextQuestion,
    currentScreenIndex,
    colorTheme,
    subjectTheme,
    handleNextScreenOnClick,
    userName,
    setUserName,
    handlePreviousScreenOnClick,
    answers,
    restartGame,
    form,
    scrollSlidesFromRight,
    currentQuestion,
    totalQuestions,
    handleOnShowResultModal,
}) => {
    const ScreenComponents = {
        ScreenStart,
        ScreenName,
        ScreenInfo,
        ScreenSingleChoice,
        ScreenSingleChoiceWithMedia,
        ScreenMultiChoiceWithMedia,
        ScreenMultiChoice,
        ScreenSingleImageChoice,
        ScreenMultiImageChoice,
        ScreenSingleRangeChoice,
        ScreenResult,
    };

    return (
        screens.map((item, index) => {
            const ScreenComponent = ScreenComponents[item.component];
            return (
                <section key={index} className={classNames(
                    "App__Screen", {
                    "App__Screen--Active": currentScreenIndex === index,
                    "App__Screen--SlideFromLeft": currentScreenIndex === index && !scrollSlidesFromRight,
                    "App__Screen--SlideFromRight": currentScreenIndex === index && scrollSlidesFromRight,
                })}>
                    <ScreenComponent
                        {...item}
                        logo={logo}
                        colorTheme={colorTheme}
                        subjectTheme={subjectTheme}
                        userName={userName}
                        setUserName={setUserName}
                        handlePreviousScreenOnClick={handlePreviousScreenOnClick}
                        handleNextScreenOnClick={handleNextScreenOnClick}
                        currentScreenIndex={currentScreenIndex}
                        screenIndex={index}
                        totalScreenIndexes={screens.length - 1}
                        currentQuestion={currentQuestion}
                        totalQuestions={totalQuestions}
                        handleAnswerOnNextQuestion={handleAnswerOnNextQuestion}
                        answers={answers}
                        restartGame={restartGame}
                        form={form}
                        screens={screens}
                        handleOnShowResultModal={handleOnShowResultModal}
                    />
                </section>
            )
        })
    )
}

const Illustrations = ({ subjectTheme }) => {
    const illustrationsPathWithSubjectPrefix = `${process.env.PUBLIC_URL}/illustrations/${subjectTheme}`;
    const showExtraBottomLeft = ['fly', 'shipping'].includes(subjectTheme);

    return (
        <>
            <img
                alt="Illustration"
                className={`App__Illustration App__Illustration--TopLeft App__Illustration--${ucFirst(subjectTheme)}`}
                src={`${illustrationsPathWithSubjectPrefix}--top-left.svg`}
            />
            <img
                alt="Illustration"
                className={`App__Illustration App__Illustration--TopRight App__Illustration--${ucFirst(subjectTheme)}`}
                src={`${illustrationsPathWithSubjectPrefix}--top-right.svg`}
            />
            <img
                alt="Illustration"
                className={`App__Illustration App__Illustration--BottomLeft App__Illustration--${ucFirst(subjectTheme)}`}
                src={`${illustrationsPathWithSubjectPrefix}--bottom-left.svg`}
            />
            {showExtraBottomLeft && (
                <img
                    alt="Illustration"
                    className={`App__Illustration App__Illustration--BottomLeftExtra App__Illustration--${ucFirst(subjectTheme)}`}
                    src={`${illustrationsPathWithSubjectPrefix}--bottom-left-extra.svg`}
                />
            )}
            <img
                alt="Illustration"
                className={`App__Illustration App__Illustration--BottomRight App__Illustration--${ucFirst(subjectTheme)}`}
                src={`${illustrationsPathWithSubjectPrefix}--bottom-right.svg`}
            />
            <img
                alt="Illustration"
                className={`App__Illustration App__Illustration--Final App__Illustration--FinalBottomLeft App__Illustration--${ucFirst(subjectTheme)}`}
                src={`${illustrationsPathWithSubjectPrefix}--final-bottom-left.svg`}
            />
            <img alt="Illustration"
                className={`App__Illustration App__Illustration--Final App__Illustration--FinalBottomRight App__Illustration--${ucFirst(subjectTheme)}`}
                src={`${illustrationsPathWithSubjectPrefix}--final-bottom-right.svg`}
            />
        </>
    )
}

App.defaultProps = {
    colorTheme: "",
}

export default App;
